import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminCoachTrainingListResult,
  type AdminCoachTrainingRow,
  type AdminCoachTrainingStage,
} from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Input,
  Modal,
  NUMERIC,
  SectionHeader,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
  Toggle,
} from "pages/admin/ui";
import { todayInputValue } from "pages/admin/user/userFormat";
import {
  canSubmitSimulatedRows,
  isCoachEmail,
  ORWELL_EMAIL_DOMAIN,
  orwellEmailOf,
  SIMULATED_MAX_ROWS,
  simulatedRowErrors,
  type SimulatedPatientFormRow,
} from "./coachFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";
const COLUMN_COUNT = 6;
// 휴가 끝난 다음날 크론이 되돌리는 일일 캡과 같다
const DEFAULT_DAILY_CAP = 50;

const toCount = (value: string) => Math.max(0, Math.floor(Number(value) || 0));

const STAGE: Record<
  AdminCoachTrainingStage,
  { label: string; tone: "warn" | "info" | "ok" }
> = {
  signup_pending: { label: "가입 대기", tone: "warn" },
  account_ready: { label: "계정 세팅 완료", tone: "info" },
  simulated_patient_pending: { label: "모의 내담자 대기", tone: "warn" },
  training: { label: "교육 중", tone: "ok" },
};

/**
 * 코치 관리: 교육. 실무 투입 전 코치를 추가하고 진행 단계를 본다.
 * 추가하면 가입 대기 → 그 이메일로 구글 가입하면 서버가 코치 계정·센드버드 유저를 세팅한다 (계정 세팅 완료)
 * → 모의 내담자 등록 (모의 내담자 대기) → 그 이메일로 구글 가입하면 서버가 이 코치에게 배정해 프로그램을 시작시킨다 (교육 중)
 * → 합격(실무로) 또는 탈락(계정 탈퇴, 실무 비활성). 둘 다 교육 목록에서 빠지고 모의 내담자는 종료된다.
 */
export default function CoachTrainingSection() {
  const [data, setData] = useState<AdminCoachTrainingListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [simulatedFor, setSimulatedFor] =
    useState<AdminCoachTrainingRow | null>(null);
  const [passFor, setPassFor] = useState<AdminCoachTrainingRow | null>(null);
  const [failFor, setFailFor] = useState<AdminCoachTrainingRow | null>(null);

  // 추가 직후 다시 받는 요청과 겹쳐도 마지막 응답만 반영한다
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingList();
      if (seq !== requestSeq.current) return;
      if (res.code === 0 && res.result) setData(res.result);
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const code = (e as AxiosError).response?.status;
      setError(
        code === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const coaches = data?.coaches ?? [];

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="교육 코치" count={`${coaches.length}명`}>
        <Button variant="primary" onClick={() => setCreating(true)}>
          신규 코치 추가
        </Button>
      </SectionHeader>

      <p className="text-sm text-slate-500">
        실무 투입 전 코치예요. 추가한 이메일로 구글 가입하면 코치 계정이
        세팅돼요. 합격하면 실무로 넘어가고, 탈락하면 코치 계정이 탈퇴돼요.
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Table>
        <THead>
          <TH>코치</TH>
          <TH>이메일</TH>
          <TH numeric>회원번호</TH>
          <TH help="가입 대기: 추가만 하고 아직 가입 전 → 계정 세팅 완료: 그 이메일로 구글 가입해 코치 계정·채팅 계정이 만들어졌어요 → 모의 내담자 대기: 모의 내담자를 등록했고 그 이메일로 가입 전 → 교육 중: 모의 내담자가 가입해 이 코치에게 배정되고 프로그램이 시작됐어요.">
            단계
          </TH>
          <TH help="코치 교육용 모의 내담자 계정(여러 명). 마스터 플랜·내부자로 이 코치에게 배정돼요.">
            모의 내담자
          </TH>
          <TH help="합격: 계약 시작일·캡을 넣고 활성으로 실무에 넘겨요 (교보재 꺼짐, 배정은 캡으로 조정). 탈락: 코치 계정을 탈퇴 처리하고 실무 목록에 비활성으로 남겨요. 둘 다 이용 중인 모의 내담자는 종료돼요.">
            교육 결과
          </TH>
        </THead>
        <tbody>
          {loading && !data ? (
            <TSkeleton colSpan={COLUMN_COUNT} />
          ) : coaches.length === 0 ? (
            <TEmpty colSpan={COLUMN_COUNT}>교육 중인 코치가 없어요</TEmpty>
          ) : (
            coaches.map((row) => (
              <TRow key={row.email}>
                <TD>
                  <span className="font-medium text-slate-900">{row.name}</span>
                </TD>
                <TD>
                  <span className="text-slate-600">{row.email}</span>
                </TD>
                <TD numeric>
                  {row.coachId ?? <span className="text-slate-300">-</span>}
                </TD>
                <TD>
                  <Badge tone={STAGE[row.stage].tone}>
                    {STAGE[row.stage].label}
                  </Badge>
                </TD>
                <TD>
                  {row.coachId == null ? (
                    <span className="text-slate-300">-</span>
                  ) : (
                    <span className="flex flex-col items-start gap-2">
                      {row.simulatedPatients.map((patient) => (
                        <span
                          key={`${patient.email}-${patient.userId ?? "pending"}`}
                          className="flex flex-col"
                        >
                          <span className="flex items-center gap-1.5 text-slate-900">
                            {patient.nickname}
                            {patient.userId != null ? (
                              <span className={`text-slate-500 ${NUMERIC}`}>
                                {patient.userId}
                              </span>
                            ) : (
                              <Badge tone="warn">가입 대기</Badge>
                            )}
                          </span>
                          <span className="text-xs text-slate-500">
                            {patient.email}
                          </span>
                        </span>
                      ))}
                      <Button size="sm" onClick={() => setSimulatedFor(row)}>
                        모의 내담자 추가
                      </Button>
                    </span>
                  )}
                </TD>
                <TD>
                  {row.coachId == null ? (
                    <span className="text-slate-300">-</span>
                  ) : (
                    <span className="flex gap-2">
                      <Button size="sm" onClick={() => setPassFor(row)}>
                        합격
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setFailFor(row)}
                      >
                        탈락
                      </Button>
                    </span>
                  )}
                </TD>
              </TRow>
            ))
          )}
        </tbody>
      </Table>

      {creating && (
        <CoachCreateModal
          onCreated={() => {
            setCreating(false);
            load();
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {simulatedFor?.coachId != null && (
        <SimulatedPatientModal
          coachId={simulatedFor.coachId}
          coachName={simulatedFor.name}
          onCreated={() => {
            setSimulatedFor(null);
            load();
          }}
          onFailed={load}
          onClose={() => setSimulatedFor(null)}
        />
      )}

      {passFor?.coachId != null && (
        <TrainingPassModal
          coachId={passFor.coachId}
          coachName={passFor.name}
          onDone={() => {
            setPassFor(null);
            load();
          }}
          onClose={() => setPassFor(null)}
        />
      )}

      {failFor?.coachId != null && (
        <TrainingFailModal
          coachId={failFor.coachId}
          coachName={failFor.name}
          onDone={() => {
            setFailFor(null);
            load();
          }}
          onClose={() => setFailFor(null)}
        />
      )}
    </section>
  );
}

function CoachCreateModal({
  onCreated,
  onClose,
}: {
  onCreated: () => void;
  onClose: () => void;
}) {
  // 아이디만 받는다 (도메인 고정)
  const [emailId, setEmailId] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = orwellEmailOf(emailId);
  // 칸을 비운 채로는 오류를 띄우지 않는다. 형식이 틀린 값을 넣었을 때만 알린다
  const emailError =
    emailId.trim() !== "" && !isCoachEmail(email)
      ? "coach{숫자} 형식으로 입력해 주세요"
      : null;
  const canSubmit = isCoachEmail(email) && name.trim() !== "";

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingCreate(email, name.trim());
      if (res.code === 0) onCreated();
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="신규 코치 추가"
      description="가입 대기로 추가돼요. 이 이메일로 구글 가입하면 코치 계정이 세팅돼요."
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          disabled={!canSubmit}
          loading={saving}
          loadingLabel="추가 중"
          onClick={submit}
        >
          추가
        </Button>
      }
    >
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="코치 이메일" error={emailError}>
          <EmailIdInput
            placeholder="coach12"
            value={emailId}
            invalid={emailError != null}
            onChange={setEmailId}
          />
        </Field>
        <Field label="코치 이름">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {/* Enter 로 제출 */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}

function SimulatedPatientModal({
  coachId,
  coachName,
  onCreated,
  onFailed,
  onClose,
}: {
  coachId: number;
  coachName: string;
  onCreated: () => void;
  // 일부만 처리됐을 수 있어 실패해도 목록을 다시 받는다
  onFailed: () => void;
  onClose: () => void;
}) {
  // 아이디만 받는다 (도메인 고정). 여러 명을 한 번에 등록한다
  const [rows, setRows] = useState<SimulatedPatientFormRow[]>([
    { emailId: "", nickname: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errors = simulatedRowErrors(rows);
  const canSubmit = canSubmitSimulatedRows(rows);

  const updateRow = (index: number, patch: Partial<SimulatedPatientFormRow>) =>
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.coachSimulatedPatientCreate(
        coachId,
        rows.map((row) => ({
          email: orwellEmailOf(row.emailId),
          nickname: row.nickname.trim(),
        }))
      );
      if (res.code === 0) {
        onCreated();
      } else {
        setError(res.message || FALLBACK_ERROR_MESSAGE);
        onFailed();
      }
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
      onFailed();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="모의 내담자 추가"
      subject={coachName}
      description="그 이메일로 구글 로그인하면 새 계정으로 시작돼요 (마스터 플랜·내부자, 이 코치에게 배정). 이미 가입된 계정이 있으면 탈퇴 처리돼요. 이용 중인 계정은 등록할 수 없어요."
      width="560px"
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          disabled={!canSubmit}
          loading={saving}
          loadingLabel="등록 중"
          onClick={submit}
        >
          {rows.length}명 등록
        </Button>
      }
    >
      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid grid-cols-[1fr_9rem_2rem] gap-2 text-xs font-medium text-slate-600">
          <span>모의 내담자 이메일</span>
          <span>닉네임</span>
        </div>
        {rows.map((row, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="grid grid-cols-[1fr_9rem_2rem] items-center gap-2">
              <EmailIdInput
                placeholder="user31"
                value={row.emailId}
                invalid={errors[i].email != null}
                autoFocus={i === rows.length - 1}
                onChange={(emailId) => updateRow(i, { emailId })}
              />
              <Input
                aria-label="닉네임"
                value={row.nickname}
                invalid={errors[i].nickname != null}
                onChange={(e) => updateRow(i, { nickname: e.target.value })}
              />
              {rows.length > 1 && (
                <button
                  type="button"
                  aria-label="이 줄 빼기"
                  className="rounded-md px-2 py-1 text-lg leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  onClick={() =>
                    setRows((prev) => prev.filter((_, j) => j !== i))
                  }
                >
                  ×
                </button>
              )}
            </div>
            {(errors[i].email || errors[i].nickname) && (
              <span className="text-xs text-rose-600">
                {errors[i].email ?? errors[i].nickname}
              </span>
            )}
          </div>
        ))}
        {rows.length < SIMULATED_MAX_ROWS && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={() =>
              setRows((prev) => [...prev, { emailId: "", nickname: "" }])
            }
          >
            + 한 명 더
          </Button>
        )}
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {/* Enter 로 제출 */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}

// 합격: 계약 시작일·배정 설정을 넣고 실무로 넘긴다. 서버가 항상 활성으로 한다 — 배정은 캡으로만 조정한다 (계약 시작 전에도 배정될 수 있다)
function TrainingPassModal({
  coachId,
  coachName,
  onDone,
  onClose,
}: {
  coachId: number;
  coachName: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    startDate: todayInputValue(),
    assignCapCount: 0,
    assignDailyCapCount: DEFAULT_DAILY_CAP,
    isMasterPlanExcluded: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = form.startDate !== "";

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingPass({ coachId, ...form });
      if (res.code === 0) onDone();
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="교육 합격"
      subject={coachName}
      description="활성 코치로 실무 목록에 넘어가요. 신규 배정은 캡으로 조정돼요 (계약 시작 전에도 배정될 수 있어요). 교보재가 꺼지고, 이용 중인 모의 내담자는 종료돼요 (가입 전 모의 내담자는 지워져요)."
      width="560px"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={saving}
            loadingLabel="처리 중"
            onClick={submit}
          >
            합격 처리할게요
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        <Field label="계약 시작일">
          <Input
            type="date"
            value={form.startDate}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, startDate: e.target.value }))
            }
          />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="일일 캡"
            helper="하루에 새로 받는 최대 인원. 0이면 신규 배정이 멈춰요."
          >
            <Input
              type="number"
              min={0}
              step={1}
              className={NUMERIC}
              value={form.assignDailyCapCount}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  assignDailyCapCount: toCount(e.target.value),
                }))
              }
            />
          </Field>
          <Field
            label="전체 캡"
            helper={
              form.assignCapCount === 0 ? (
                <span className="text-amber-700">
                  0이면 자동 배정을 받지 않아요 (초과 배정에서도 빠져요).
                </span>
              ) : (
                "담당 인원이 여기 닿으면 자동 배정 후보에서 빠져요."
              )
            }
          >
            <Input
              type="number"
              min={0}
              step={1}
              className={NUMERIC}
              value={form.assignCapCount}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  assignCapCount: toCount(e.target.value),
                }))
              }
            />
          </Field>
        </div>
        <Toggle
          checked={!form.isMasterPlanExcluded}
          onChange={(next) =>
            setForm((prev) => ({ ...prev, isMasterPlanExcluded: !next }))
          }
          label="마스터 플랜 담당"
          helper="끄면 마스터 플랜 내담자는 이 코치에게 배정되지 않아요."
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

// 탈락: 코치 계정을 탈퇴 처리하고 실무 목록에 비활성으로 남긴다. 되돌릴 수 없어 확인만 받는다
function TrainingFailModal({
  coachId,
  coachName,
  onDone,
  onClose,
}: {
  coachId: number;
  coachName: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.coachTrainingFail(coachId);
      if (res.code === 0) onDone();
      else setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="교육 탈락"
      subject={coachName}
      description="코치 계정이 탈퇴 처리돼 더는 로그인할 수 없어요. 실무 목록에 비활성으로 남고, 이용 중인 모의 내담자는 종료돼요 (가입 전 모의 내담자는 지워져요). 되돌릴 수 없어요."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="danger"
            loading={saving}
            loadingLabel="처리 중"
            onClick={submit}
          >
            탈락 처리할게요
          </Button>
        </>
      }
    >
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </Modal>
  );
}

// 이메일 아이디 입력 + 고정 도메인(@orwellhealth.org) 표시
function EmailIdInput({
  placeholder,
  value,
  invalid,
  autoFocus = true,
  onChange,
}: {
  placeholder: string;
  value: string;
  invalid: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Input
        className={NUMERIC}
        placeholder={placeholder}
        value={value}
        invalid={invalid}
        autoFocus={autoFocus}
        aria-label="이메일 아이디"
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="shrink-0 text-sm text-slate-500">
        {ORWELL_EMAIL_DOMAIN}
      </span>
    </span>
  );
}
